import { useState, useEffect } from 'react';
import { BottomSheet } from '../common/BottomSheet';
import { ACTIVITY_META, type Activity } from '../../types/activity';

interface ActivityEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: Activity | null;
  onSave: (activityId: string, note: string | null) => Promise<void>;
  onDelete: (activityId: string) => Promise<void>;
}

export function ActivityEditSheet({ open, onOpenChange, activity, onSave, onDelete }: ActivityEditSheetProps) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (activity && open) {
      setNote(activity.note ?? '');
    }
  }, [activity, open]);

  if (!activity) return null;

  const meta = ACTIVITY_META[activity.type];

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    await onSave(activity.id, note.trim() || null);
    setSaving(false);
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    await onDelete(activity.id);
    setDeleting(false);
    onOpenChange(false);
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Activity"
      initialSnap="collapsed"
    >
      <div className="space-y-5">
        {/* Activity info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <span className={`material-symbols-outlined text-xl ${meta.color}`} aria-hidden="true">
            {meta.icon}
          </span>
          <div>
            <span className="font-sans text-sm font-semibold text-editorial-black">{meta.label}</span>
            <span className="font-sans text-sm text-gray-500 ml-1">{activity.plantName}</span>
          </div>
        </div>

        {/* Note edit */}
        <div>
          <label htmlFor="edit-activity-note" className="block font-sans text-xs font-medium text-gray-500 uppercase tracking-widest mb-2">
            Note
          </label>
          <textarea
            id="edit-activity-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this activity..."
            rows={3}
            maxLength={500}
            autoFocus
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-sans text-sm text-editorial-black placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-lg bg-primary text-white font-sans font-bold text-sm uppercase tracking-widest disabled:opacity-50 transition-opacity"
            aria-label="Save changes"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="py-3 px-4 rounded-lg border border-danger/30 text-danger font-sans font-bold text-sm uppercase tracking-widest hover:bg-danger/5 disabled:opacity-50 transition-all"
            aria-label="Delete activity"
          >
            {deleting ? '...' : 'Delete'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
